import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Users, Search, Shield, X, Eye, Mail, Calendar, Phone, MapPin, CheckCircle, AlertCircle, User } from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner';

export default function UsersView() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  // Calculate profile completion percentage
  const calculateProfileCompletion = (user: any) => {
    // Helper to check if a field has meaningful value
    const hasValue = (value: any) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return value === true;
      return Boolean(value);
    };

    const fields = [
      { name: 'Name', value: user.fullName || user.displayName || user.name },
      { name: 'Email', value: user.email },
      { name: 'Phone', value: user.phoneNumber || user.phone },
      { name: 'Photo', value: user.photoURL || user.photoUrl },
      { name: 'Addresses', value: user.addresses || user.address },
      { name: 'City', value: user.city },
      { name: 'State', value: user.state },
      { name: 'Pincode', value: user.pincode || user.postalCode || user.zipCode },
      { name: 'Email Verified', value: user.isEmailVerified || user.emailVerified }
    ];
    
    // Only log for debugging when explicitly needed (disabled in production)
    // Uncomment the lines below only when debugging specific user issues
    /*
    const missingFields = fields.filter(f => !hasValue(f.value)).map(f => f.name);
    if (missingFields.length > 0 && process.env.NODE_ENV === 'development') {
      console.log(`User ${user.email || user.id} missing:`, missingFields);
    }
    */
    
    const filledFields = fields.filter(f => hasValue(f.value)).length;
    const totalFields = fields.length;
    
    return Math.round((filledFields / totalFields) * 100);
  };

  // Get completion status
  const getCompletionStatus = (percentage: number) => {
    if (percentage === 100) return { label: 'Complete', color: 'text-green-600 bg-green-100', icon: CheckCircle };
    if (percentage >= 50) return { label: 'Partial', color: 'text-yellow-600 bg-yellow-100', icon: AlertCircle };
    return { label: 'Incomplete', color: 'text-red-600 bg-red-100', icon: AlertCircle };
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'users'));
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by created date
      usersData.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, role: string) => {
    try {
      setActionLoading(true);
      await updateDoc(doc(db, 'users', userId), {
        role,
        updatedAt: new Date().toISOString()
      });
      await loadUsers();
      alert(`User role updated to ${role}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const styles = {
      super_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      moderator: 'bg-green-100 text-green-800',
      service_provider: 'bg-indigo-100 text-indigo-800',
      viewer: 'bg-gray-100 text-gray-800',
      user: 'bg-yellow-100 text-yellow-800'
    };
    const displayName = role === 'service_provider' ? 'Service Provider' : role?.replace('_', ' ').toUpperCase() || 'USER';
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[role as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {displayName}
      </span>
    );
  };

  const filteredUsers = users.filter((user: any) => {
    if (roleFilter !== 'all' && user.role !== roleFilter) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(search) ||
      user.fullName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.role?.toLowerCase().includes(search) ||
      user.phoneNumber?.toLowerCase().includes(search)
    );
  });

  // Calculate statistics
  const totalUsers = users.length;
  const completeProfiles = users.filter(u => calculateProfileCompletion(u) === 100).length;
  const incompleteProfiles = totalUsers - completeProfiles;
  const averageCompletion = totalUsers > 0 
    ? Math.round(users.reduce((sum, u) => sum + calculateProfileCompletion(u), 0) / totalUsers)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
        <p className="text-gray-600 mt-1">View and manage platform users</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
            </div>
            <Users className="h-8 w-8 text-primary-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Complete Profiles</p>
              <p className="text-2xl font-bold text-green-600">{completeProfiles}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Incomplete Profiles</p>
              <p className="text-2xl font-bold text-red-600">{incompleteProfiles}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Completion</p>
              <p className="text-2xl font-bold text-primary-600">{averageCompletion}%</p>
            </div>
            <User className="h-8 w-8 text-primary-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setRoleFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                roleFilter === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setRoleFilter('super_admin')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                roleFilter === 'super_admin' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Super Admin
            </button>
            <button
              onClick={() => setRoleFilter('admin')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                roleFilter === 'admin' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Admin
            </button>
            <button
              onClick={() => setRoleFilter('user')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                roleFilter === 'user' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Users
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">
              {searchTerm || roleFilter !== 'all' ? 'Try adjusting your filters' : 'No users in the system'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profile Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user: any) => {
                  const completion = calculateProfileCompletion(user);
                  const status = getCompletionStatus(completion);
                  const StatusIcon = status.icon;
                  const displayName = user.displayName || user.fullName || user.email?.split('@')[0] || 'No Name';
                  
                  return (
                  <motion.tr
                    key={user.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {user.photoURL ? (
                          <img src={user.photoURL} alt="" className="h-10 w-10 rounded-full mr-3" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                            <span className="text-primary-600 font-semibold">
                              {displayName.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{displayName}</div>
                          <div className="text-sm text-gray-500">ID: {user.id.slice(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{user.email}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {user.isEmailVerified ? (
                          <span className="flex items-center text-xs text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </span>
                        ) : (
                          <span className="flex items-center text-xs text-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Verified
                          </span>
                        )}
                      </div>
                      {user.phoneNumber && (
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <Phone className="h-3 w-3 mr-1" />
                          {user.phoneNumber}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role || 'user')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </span>
                        <div className="flex-1 min-w-[60px]">
                          <div className="text-xs font-medium text-gray-700 mb-1">{completion}%</div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${
                                completion === 100 ? 'bg-green-600' : 
                                completion >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${completion}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      <AnimatePresence>
        {selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedUser(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Profile Completion Status */}
                  {(() => {
                    const completion = calculateProfileCompletion(selectedUser);
                    const status = getCompletionStatus(completion);
                    const StatusIcon = status.icon;
                    const displayName = selectedUser.displayName || selectedUser.fullName || selectedUser.name || selectedUser.email?.split('@')[0] || 'No Name';
                    
                    // Helper to check if field has meaningful value (same as in calculateProfileCompletion)
                    const hasValue = (value: any) => {
                      if (value === null || value === undefined) return false;
                      if (typeof value === 'string') return value.trim().length > 0;
                      if (Array.isArray(value)) return value.length > 0;
                      if (typeof value === 'boolean') return value === true;
                      return Boolean(value);
                    };
                    
                    return (
                      <div className="p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-200">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">Profile Completion</h3>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                            <StatusIcon className="h-4 w-4 mr-1" />
                            {status.label}
                          </span>
                        </div>
                        <div className="mb-2">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Overall Progress</span>
                            <span className="font-semibold text-gray-900">{completion}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all ${
                                completion === 100 ? 'bg-green-600' : 
                                completion >= 50 ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${completion}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                          <div className={`flex items-center ${hasValue(selectedUser.fullName || selectedUser.displayName || selectedUser.name) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.fullName || selectedUser.displayName || selectedUser.name) ? '✓' : '✗'} Full Name
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.email) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.email) ? '✓' : '✗'} Email
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.phoneNumber || selectedUser.phone) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.phoneNumber || selectedUser.phone) ? '✓' : '✗'} Phone
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.photoURL || selectedUser.photoUrl) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.photoURL || selectedUser.photoUrl) ? '✓' : '✗'} Photo
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.addresses || selectedUser.address) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.addresses || selectedUser.address) ? '✓' : '✗'} Address
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.city) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.city) ? '✓' : '✗'} City
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.state) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.state) ? '✓' : '✗'} State
                          </div>
                          <div className={`flex items-center ${hasValue(selectedUser.pincode || selectedUser.postalCode || selectedUser.zipCode) ? 'text-green-600' : 'text-gray-400'}`}>
                            {hasValue(selectedUser.pincode || selectedUser.postalCode || selectedUser.zipCode) ? '✓' : '✗'} Pincode
                          </div>
                          <div className={`flex items-center ${(selectedUser.isEmailVerified === true || selectedUser.emailVerified === true) ? 'text-green-600' : 'text-gray-400'}`}>
                            {(selectedUser.isEmailVerified === true || selectedUser.emailVerified === true) ? '✓' : '✗'} Email Verified
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* User Profile */}
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    {(selectedUser.photoURL || selectedUser.photoUrl) ? (
                      <img src={selectedUser.photoURL || selectedUser.photoUrl} alt="" className="h-20 w-20 rounded-full object-cover" />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-primary-600 font-bold text-3xl">
                          {(selectedUser.displayName || selectedUser.fullName || selectedUser.name || selectedUser.email)?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedUser.displayName || selectedUser.fullName || selectedUser.name || selectedUser.email?.split('@')[0] || 'No Name'}
                      </h3>
                      <p className="text-gray-600 flex items-center mt-1">
                        <Mail className="h-4 w-4 mr-2" />
                        {selectedUser.email}
                        {(selectedUser.isEmailVerified || selectedUser.emailVerified) ? (
                          <span className="ml-2 flex items-center text-xs text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </span>
                        ) : (
                          <span className="ml-2 flex items-center text-xs text-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Not Verified
                          </span>
                        )}
                      </p>
                      {(selectedUser.phoneNumber || selectedUser.phone) && (
                        <p className="text-gray-600 flex items-center mt-1">
                          <Phone className="h-4 w-4 mr-2" />
                          {selectedUser.phoneNumber || selectedUser.phone}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 flex items-center mt-1">
                        <Calendar className="h-4 w-4 mr-2" />
                        Joined: {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Current Role */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <Shield className="h-5 w-5 mr-2" />
                      Current Role
                    </h3>
                    {getRoleBadge(selectedUser.role || 'user')}
                  </div>

                  {/* User ID */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-1">User ID</h3>
                    <p className="text-sm font-mono text-gray-700">{selectedUser.id}</p>
                  </div>

                  {/* Additional Info */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <User className="h-5 w-5 mr-2" />
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-sm">Full Name</h4>
                        <p className="text-gray-700">{selectedUser.fullName || selectedUser.displayName || selectedUser.name || 'Not provided'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-1 text-sm">Phone Number</h4>
                        <p className="text-gray-700">{selectedUser.phoneNumber || selectedUser.phone || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  {((selectedUser.addresses?.length > 0) || (selectedUser.address?.length > 0) || selectedUser.city || selectedUser.state || (selectedUser.pincode || selectedUser.postalCode || selectedUser.zipCode) || selectedUser.country) && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <MapPin className="h-5 w-5 mr-2" />
                        Address Information
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(selectedUser.addresses?.length > 0 || selectedUser.address?.length > 0) && (
                          <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Saved Addresses</h4>
                            <div className="space-y-2 mt-2">
                              {(selectedUser.addresses || selectedUser.address || []).map((addr: any, idx: number) => (
                                <div key={idx} className="text-gray-700 text-sm border-l-2 border-primary-500 pl-3">
                                  {typeof addr === 'string' ? addr : `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.pincode || addr.postalCode || addr.zipCode || ''}`}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedUser.city && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">City</h4>
                            <p className="text-gray-700">{selectedUser.city}</p>
                          </div>
                        )}
                        {selectedUser.state && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">State</h4>
                            <p className="text-gray-700">{selectedUser.state}</p>
                          </div>
                        )}
                        {(selectedUser.pincode || selectedUser.postalCode || selectedUser.zipCode) && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Pincode</h4>
                            <p className="text-gray-700">{selectedUser.pincode || selectedUser.postalCode || selectedUser.zipCode}</p>
                          </div>
                        )}
                        {selectedUser.country && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm">Country</h4>
                            <p className="text-gray-700">{selectedUser.country}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Change to Service Provider */}
                  {selectedUser.role !== 'service_provider' && (
                    <div className="pt-6 border-t border-gray-200">
                      <h3 className="font-semibold text-gray-900 mb-3">Change User Role</h3>
                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to make this user a Service Provider?')) {
                            updateUserRole(selectedUser.id, 'service_provider');
                            setSelectedUser(null);
                          }
                        }}
                        disabled={actionLoading}
                        className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Shield className="h-5 w-5 inline mr-2" />
                        Make Service Provider
                      </button>
                    </div>
                  )}

                  {selectedUser.role === 'service_provider' && (
                    <div className="pt-6 border-t border-gray-200">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                        <Shield className="h-8 w-8 text-green-600 mx-auto mb-2" />
                        <p className="text-green-800 font-medium">This user is already a Service Provider</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
